import React, { useState, useEffect, useCallback } from 'react';
import { Crown, RefreshCw, Clock, CheckCircle2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const GOLD = '#c9a962';

export const FoundingTenTracker = () => {
  const [data, setData] = useState(null);
  const [wall, setWall] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [lb, w] = await Promise.all([
        fetch(`${API}/api/admin/founding-ten/leaderboard`).then(r => r.ok ? r.json() : null),
        fetch(`${API}/api/founding-ten/wall`).then(r => r.ok ? r.json() : null),
      ]);
      setData(lb);
      setWall(w);
    } catch (e) {} finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const winners = wall?.winners || [];
  const slots = Array.from({ length: 10 }, (_, i) => winners.find(w => w.rank === i + 1) || null);
  const daysLeft = data?.ends_at ? Math.max(0, Math.ceil((new Date(data.ends_at) - Date.now()) / 86400000)) : '—';
  const rows = data?.leaderboard || [];

  return (
    <div className="bg-white rounded-xl p-6 shadow-sm" data-testid="founding-ten-tracker">
      <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
        <h3 className="font-semibold text-gray-900 flex items-center gap-2">
          <Crown className="w-5 h-5" style={{ color: GOLD }} />
          Founding Ten Tracker
        </h3>
        <div className="flex items-center gap-3">
          <span className="text-xs text-gray-500 flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {daysLeft} days left</span>
          <button onClick={load} className="text-gray-400 hover:text-gray-600" data-testid="founding-ten-tracker-refresh">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>
      <p className="text-sm text-gray-500 mb-4">Who's racing for the 10 slots — 20 listings + 5 qualified referrals each.</p>

      {/* Slot strip */}
      <div className="grid grid-cols-5 sm:grid-cols-10 gap-1.5 mb-5" data-testid="founding-ten-slot-strip">
        {slots.map((w, i) => (
          <div key={i} title={w ? `#${i + 1} ${w.name}` : `Slot #${i + 1} unclaimed`}
            className="rounded-md py-2 text-center text-[10px] font-semibold"
            style={{ backgroundColor: w ? '#14100e' : '#f5f3ef', color: w ? GOLD : '#b8b0a4', border: `1px solid ${w ? GOLD : '#e8e4dc'}` }}>
            #{i + 1}
            <div className="truncate px-1 font-normal" style={{ color: w ? '#f0e9df' : '#c4bcb0' }}>
              {w ? w.name.split(' ')[0] : '—'}
            </div>
          </div>
        ))}
      </div>

      {/* Leaderboard */}
      {rows.length === 0 ? (
        <p className="text-sm text-gray-400 italic" data-testid="founding-ten-tracker-empty">
          No challenge activity yet — progress appears here as soon as members start listing.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-400 uppercase tracking-wider border-b">
                <th className="py-2 pr-3">Member</th>
                <th className="py-2 pr-3">Listings</th>
                <th className="py-2 pr-3">Referrals</th>
                <th className="py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const itemsPct = Math.min(100, (r.items / 20) * 100);
                const refsPct = Math.min(100, (r.referrals / 5) * 100);
                return (
                  <tr key={r.user_id} className="border-b last:border-0" data-testid={`founding-ten-row-${r.user_id}`}>
                    <td className="py-2.5 pr-3">
                      <p className="font-medium text-gray-900">{r.name || '—'}</p>
                      <p className="text-[11px] text-gray-400">{r.email}</p>
                    </td>
                    <td className="py-2.5 pr-3 min-w-[110px]">
                      <span className="text-xs font-semibold text-gray-700">{r.items} / 20</span>
                      <div className="h-1.5 rounded-full bg-gray-100 mt-1">
                        <div className="h-1.5 rounded-full" style={{ width: `${itemsPct}%`, backgroundColor: GOLD }} />
                      </div>
                    </td>
                    <td className="py-2.5 pr-3 min-w-[110px]">
                      <span className="text-xs font-semibold text-gray-700">{r.referrals} / 5</span>
                      <div className="h-1.5 rounded-full bg-gray-100 mt-1">
                        <div className="h-1.5 rounded-full" style={{ width: `${refsPct}%`, backgroundColor: '#1a3a2a' }} />
                      </div>
                    </td>
                    <td className="py-2.5">
                      {r.completed ? (
                        <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full" style={{ backgroundColor: '#14100e', color: GOLD }}>
                          <CheckCircle2 className="w-3 h-3" /> Founding Ten #{r.rank}
                        </span>
                      ) : (
                        <span className="text-[11px] text-gray-400">{20 - r.items + 5 - r.referrals} steps to go</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
